import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  fetchEvalExperiments,
  type EvalExperimentRecord,
  type EvalExperimentSummary,
  type EvalRecordEnvelope,
} from '@/admin/lib/api';
import { Badge } from '@/admin/components/ui/badge';
import { Button } from '@/admin/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/admin/components/ui/card';
import { Skeleton } from '@/admin/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/admin/components/ui/table';
import { IconRefresh } from '@tabler/icons-react';

type EvalExperimentEnvelope = EvalRecordEnvelope<
  EvalExperimentRecord,
  EvalExperimentSummary
>;

function formatDateTime(value?: string) {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

function experimentSuiteRefs(record: EvalExperimentRecord) {
  if (Array.isArray(record.suite_refs) && record.suite_refs.length > 0) {
    return record.suite_refs;
  }
  if (record.suite_ref) {
    return [record.suite_ref];
  }
  return [];
}

function formatSuiteLabel(record: EvalExperimentRecord) {
  const refs = experimentSuiteRefs(record);
  if (refs.length === 0) return '—';
  if (refs.length === 1) {
    const fileName = refs[0].split('/').pop() || refs[0];
    return fileName.replace(/\.ya?ml$/, '');
  }
  return `${refs.length} suites`;
}

function formatScore(value?: number) {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  return `${Math.round(value * 100)}%`;
}

function formatPassCurve(record: EvalExperimentRecord, key: string) {
  return formatScore(record.aggregate_metrics?.[key]);
}

function formatCurveLabel(key: string): string {
  if (key.startsWith('pass_at_')) return `pass@${key.slice('pass_at_'.length)}`;
  if (key.startsWith('pass_hat_'))
    return `pass^${key.slice('pass_hat_'.length)}`;
  return key;
}

function extractCurveKeys(metrics?: Record<string, number>): string[] {
  return Object.keys(metrics || {})
    .filter(key => key.startsWith('pass_at_') || key.startsWith('pass_hat_'))
    .sort((left, right) => {
      const lp = left.startsWith('pass_at_') ? 0 : 1;
      const rp = right.startsWith('pass_at_') ? 0 : 1;
      if (lp !== rp) return lp - rp;
      const lk = Number.parseInt(left.split('_').pop() || '0', 10);
      const rk = Number.parseInt(right.split('_').pop() || '0', 10);
      return lk - rk;
    });
}

export const EvalsPage: React.FC = () => {
  const navigate = useNavigate();
  const [experiments, setExperiments] = useState<EvalExperimentEnvelope[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (background = false) => {
    if (background) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      const records = await fetchEvalExperiments();
      records.sort(
        (left, right) =>
          new Date(right.received_at).getTime() -
          new Date(left.received_at).getTime()
      );
      setExperiments(records);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load eval experiments'
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-[420px] w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 overflow-y-auto h-full">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Evaluator Runs</h1>
          <p className="text-muted-foreground">
            New experiment records ingested from agent-evaluator.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => load(true)}
          disabled={refreshing}
        >
          <IconRefresh className="mr-2 h-4 w-4" />
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Experiments</CardTitle>
        </CardHeader>
        <CardContent>
          {error ? (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Experiment</TableHead>
                  <TableHead>Suite</TableHead>
                  <TableHead>Adapter</TableHead>
                  <TableHead>Environment</TableHead>
                  <TableHead>Trials</TableHead>
                  <TableHead>Grading</TableHead>
                  <TableHead>Parallelism</TableHead>
                  <TableHead>Received</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {experiments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="py-10 text-center">
                      <p className="text-muted-foreground">
                        No eval experiments yet
                      </p>
                    </TableCell>
                  </TableRow>
                ) : (
                  experiments.map(experiment => {
                    const summary = experiment.summary;
                    const record = experiment.record;
                    const suiteRefs = experimentSuiteRefs(record);
                    return (
                      <TableRow
                        key={record.id}
                        className="cursor-pointer hover:bg-muted/40"
                        onClick={() => navigate(`/evals/${record.id}`)}
                      >
                        <TableCell>
                          <div className="space-y-1">
                            <div className="font-medium">{record.id}</div>
                            <div className="text-xs text-muted-foreground">
                              {record.revision_set?.evals_sha || 'DEV'} /{' '}
                              {record.revision_set?.willow_sha || 'local-dev'} /{' '}
                              {record.revision_set?.cueboard_sha || 'local-dev'}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="font-medium">
                            {formatSuiteLabel(record)}
                          </span>
                          <p className="text-xs text-muted-foreground">
                            {suiteRefs.length > 0 ? suiteRefs.join(', ') : '—'}
                          </p>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {summary?.adapter || 'unknown'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm">
                            {summary?.environment_mode || '—'}
                          </span>
                        </TableCell>
                        <TableCell>{summary?.trial_count ?? '—'}</TableCell>
                        <TableCell>
                          <div className="space-y-1 text-sm">
                            <div>
                              {record.aggregate_metrics?.graded_trial_count ? (
                                <>
                                  {record.aggregate_metrics
                                    .passing_trial_count ?? 0}
                                  /{record.aggregate_metrics.graded_trial_count}{' '}
                                  passed
                                </>
                              ) : (
                                '—'
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              avg{' '}
                              {formatScore(
                                record.aggregate_metrics?.average_trial_score
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {extractCurveKeys(record.aggregate_metrics).map(
                                (key, idx) => (
                                  <span key={key}>
                                    {idx > 0 && ' · '}
                                    {formatCurveLabel(key)}{' '}
                                    {formatPassCurve(record, key)}
                                  </span>
                                )
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{summary?.max_parallelism ?? '—'}</TableCell>
                        <TableCell>
                          {formatDateTime(experiment.received_at)}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
